import { useQueries, useQuery, UseQueryOptions } from "@tanstack/react-query"
import { AxiosError } from "axios"

import { IEventParams } from "../entities/calendar/hooks"
import { GetAllEventsResponseType } from "../entities/calendar/types"
import { getAllEvents } from "../services/CalendarServices"
import { getActiveProducts, getActiveProductsForMembership } from "../services/OrderServices"
import { getProductInfoById } from "../services/ProductServices"
import { ProductCategoryType, ProductType } from "../types/OrderTypes"
import { CacheKeys } from "../utils/CacheKeys"

export interface GenericQueryOptionsType<
  responseType,
  errorResponseType = AxiosError<Record<string, string>>,
> {
  queryOptions?: Omit<UseQueryOptions<responseType, errorResponseType>, "queryFn" | "queryKey">
}

export interface UseGetActiveProductsProps extends GenericQueryOptionsType<ProductCategoryType[]> {
  isIntegratedPage: boolean
}

export const useGetActiveProducts = ({
  isIntegratedPage,
  queryOptions,
}: UseGetActiveProductsProps) => {
  return useQuery({
    ...queryOptions,
    queryKey: [CacheKeys.ACTIVE_PRODUCTS, isIntegratedPage],
    queryFn: async () => {
      const { data } = await getActiveProducts(isIntegratedPage)
      if (data.length) {
        return data
      }
      return []
    },
  })
}

export interface UseGetActiveProductsWithCustomerLoggedInProps
  extends GenericQueryOptionsType<ProductCategoryType[]> {
  isIntegratedPage: boolean
  customerId: string
}

export const useGetActiveProductsWithCustomerLoggedIn = ({
  isIntegratedPage,
  customerId,
  queryOptions,
}: UseGetActiveProductsWithCustomerLoggedInProps) => {
  return useQuery({
    ...queryOptions,
    queryKey: [CacheKeys.ACTIVE_PRODUCTS_FOR_CUSTOMER_LOGGED_IN, isIntegratedPage, customerId],
    queryFn: async () => {
      const { data } = await getActiveProducts(isIntegratedPage, customerId)
      if (data.length) {
        return data
      }
      return []
    },
  })
}

export interface UseGetActiveProductsForMembershipProps
  extends GenericQueryOptionsType<ProductCategoryType[]> {
  isIntegratedPage: boolean
}

export const useGetActiveProductsForMembership = ({
  isIntegratedPage,
  queryOptions,
}: UseGetActiveProductsForMembershipProps) => {
  return useQuery({
    ...queryOptions,
    queryKey: [CacheKeys.ACTIVE_PRODUCTS_FOR_MEMBERSHIP, isIntegratedPage],
    queryFn: async () => {
      const { data } = await getActiveProductsForMembership(isIntegratedPage)
      if (data.length) {
        return data
      }
      return []
    },
  })
}

export interface UseGetProductInfoByIdProps extends GenericQueryOptionsType<ProductType> {
  productId: string
  isIntegratedPage: boolean
}

export const useGetProductInfoById = ({
  productId,
  isIntegratedPage,
  queryOptions,
}: UseGetProductInfoByIdProps) => {
  return useQuery({
    ...queryOptions,
    queryKey: [CacheKeys.PRODUCT_DETAILS, isIntegratedPage, productId],
    queryFn: async () => {
      if (productId) {
        const { data } = await getProductInfoById(productId, {}, isIntegratedPage)
        return data
      }
      return null
    },
  })
}
interface ICalendarEventParams extends IEventParams {
  isIntegratedPage: boolean
  queryOptions?: Omit<
    UseQueryOptions<GetAllEventsResponseType[], AxiosError<Record<string, string>>>,
    "queryFn" | "queryKey"
  >
}

export const usePublicGetAllEvents = ({
  dateRanges,
  filters,
  isIntegratedPage,
  queryOptions,
}: ICalendarEventParams) => {
  return useQueries({
    queries: dateRanges.map((range) => {
      return {
        ...queryOptions,
        queryKey: ["public", "allEvents", range, filters],
        queryFn: async () => {
          const { data: allEventsResponse } = await getAllEvents(
            {
              ...range,
              ...filters,
            },
            isIntegratedPage
          )
          if (allEventsResponse.length) {
            return allEventsResponse
          }
          return []
        },
      }
    }),
    combine: (results) => {
      // Create a map to store products by their ID
      const productMap = new Map<string, GetAllEventsResponseType>()

      // Process each result and merge availabilities for the same product
      results.forEach((result) => {
        if (result.data) {
          result.data.forEach((product) => {
            const existingProduct = productMap.get(product.product_id)

            if (existingProduct) {
              // Merge availabilities for existing product
              const mergedAvailabilities = [
                ...existingProduct.availability,
                ...product.availability,
              ]

              // Update the product with merged availabilities
              productMap.set(product.product_id, {
                ...existingProduct,
                availability: mergedAvailabilities,
              })
            } else {
              // Add new product to map
              productMap.set(product.product_id, product)
            }
          })
        }
      })

      // Convert map values back to array
      return {
        data: Array.from(productMap.values()),
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
        error: results.find((result) => result.isError)?.error,
        refetch: () => {
          return results.map((result) => result.refetch())
        },
      }
    },
  })
}
